import { useEffect } from "react";
import { tierLabelToHuman } from "@memento/library/subscription";
import { Card } from "@memento/types/card";
import { type RenderConfigClip } from "@memento/types/render-config";
import {
  Feature,
  canUseFeature,
  getLowestTierSupporting,
} from "@memento/library/feature-access";
import { useEditor } from "src/components/Renderer/RendererProvider";
import { Button } from "@memento/ui/Button";
import { Input } from "@memento/ui/Form/Input";
import { Label } from "@memento/ui/Form/Label";
import { UpgradeLink } from "src/components/subscriptions/UpgradeLink";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";

type EditCaptionProps = {
  clip: RenderConfigClip;
  card: Card;
};

export const EditClipCaption = ({ clip, card }: EditCaptionProps) => {
  const { featureFlags } = useFeatureFlags();
  const {
    setClipCaptionTitle,
    setClipCaptionSubtitle,
    clipPrefetchDetailsBySegmentId,
    setFrameToSeek,
    subscription,
  } = useEditor();

  const skipToFrame =
    clipPrefetchDetailsBySegmentId[clip.segmentId]?.skipToFrame ?? 0;

  const title = clip.caption.title || "";
  const subtitle = clip.caption.subtitle || "";

  // When the title or subtitle changes, skip to the caption frame. We need to
  // do this here instead of in the change handlers because the skipToFrame
  // won't update until after the title/subtitle change is saved (at least for
  // the first character typed into the title). For the first character typed,
  // if we skip in the change handlers, we'll skip to a frame *before* the
  // caption.
  useEffect(
    () => {
      setFrameToSeek({ frame: skipToFrame, pause: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, subtitle]
  );

  function handleCaptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setClipCaptionTitle(clip.segmentId, value);
    // Don't call setFrameToSeek here. It's called in a useEffect above. See the
    // useEffect comment for details about why.
  }

  function handleDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setClipCaptionSubtitle(clip.segmentId, value);
    // Don't call setFrameToSeek here. It's called in a useEffect above. See the
    // useEffect comment for details about why.
  }

  const handleInputFocus = () => {
    setFrameToSeek({ frame: skipToFrame, pause: true });
  };

  const { label } = getLowestTierSupporting(Feature.VideoClipCustomText);

  const canEditCaption = canUseFeature(
    Feature.VideoClipCustomText,
    subscription,
    {
      card,
      featureFlags,
    }
  );

  return (
    <div className="flex w-full flex-col gap-4 text-sm">
      {canEditCaption ? (
        <>
          <Label className="flex flex-col gap-2 text-gray-700">
            {/* No label if clip type is photo because the section title already acts
        as a label */}
            <div className="font-normal">
              {clip.type === "photo" ? "" : "Name"}
            </div>
            <Input
              data-test="caption"
              value={clip.caption.title || ""}
              maxLength={40}
              placeholder="e.g., contributor's name"
              onChange={handleCaptionChange}
              onFocus={handleInputFocus}
              type="text"
              className="text-sm"
            />
          </Label>
          <Label className="flex flex-col gap-2 text-gray-700">
            <div className="font-normal">Description (Optional)</div>
            <Input
              data-test="description"
              value={clip.caption.subtitle || ""}
              maxLength={60}
              placeholder="e.g, title, location, etc"
              onChange={handleDescriptionChange}
              onFocus={handleInputFocus}
              type="text"
              className="text-sm"
            />
            {!clip.caption.title && clip.caption.subtitle ? (
              <div className="text-xs text-gray-500">
                The description will not be displayed if the{" "}
                {clip.type === "photo" ? "caption" : "name"} is empty.
              </div>
            ) : null}
          </Label>
        </>
      ) : (
        <>
          <div className="font-medium text-gray-700">
            A {tierLabelToHuman(label)} subscription is required to add a
            caption.
          </div>
          <UpgradeLink className="w-full" feature={Feature.VideoClipCustomText}>
            <Button variant="primary">Become a Subscriber</Button>
          </UpgradeLink>
        </>
      )}
    </div>
  );
};
